import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { filter, map, take } from "rxjs/operators";
import { ModalController } from "@ionic/angular";
import { RegProfileDialogComponentComponent } from "src/app/components/reg-profile-dialog-component/reg-profile-dialog-component.component";
import { CelebrationAnimationService } from "@core/celebration-animation.service";
import { PaymentService } from "src/app/services/payment.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { SyndicateRepositoryService } from "../../../syndicate/syndicate-repository.service";
import { SyndicateDataService } from "src/app/services/syndicate-data.service";
import { SyndicateTicketsComponent } from "../../../syndicate/syndicate-tickets/syndicate-tickets.component";
import { PrivateSyndicateTicketComponent } from "../../../syndicate/tickets/private-syndicate-ticket/private-syndicate-ticket.component";
import { StoreRepositoryService } from "../../store-repository.service";
import type { PostPaymentHostBridge, SqlApiCallEnvelope } from "./checkout-try-again-host-context";

@Injectable({ providedIn: null })
export class PostPaymentNavigationService {
  constructor(
    private paymentService: PaymentService,
    private authService: AuthService,
    private location: Location,
    private router: Router,
    private syndicateService: SyndicateRepositoryService,
    private syndicateDataService: SyndicateDataService,
    private modalController: ModalController,
    private voucherService: VoucherService,
    private matDialog: MatDialog,
    private storeService: StoreRepositoryService,
    private celebrationAnimationService: CelebrationAnimationService
  ) {}

  viewTicket(bridge: PostPaymentHostBridge, page: string): void {
    bridge.addNewCardInProgress = true;
    this.paymentService.updatePaymentReqAndRes(bridge.purchaseDetail$?.value, bridge.payment_status).subscribe(
      (sqlAPICall: unknown) => {
        bridge.addNewCardInProgress = false;
        const call = sqlAPICall as SqlApiCallEnvelope;
        bridge.sqlAPICallResponse = call;
        if (call && call.response && call.response.status !== "SUCCESS") {
          this.authService
            .getConstantsMsg("1015")
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((err) => {
              this.authService.errorHandlerLogs("Error showing toast:", err);
              return err;
            });

          this.location.back();
          return;
        }

        if (page === "topUp") {
          this.router.navigate(["mainTabs/tabs/store"]);
          return;
        }

        if (page === "syndicate") {
          this.syndicateService.getPrivateSyndicateProducts(bridge.purchaseDetail$?.value?.psId).pipe(take(1)).subscribe(
            async (product) => {
              bridge.addNewCardInProgress = false;
              const syndicate = bridge.purchaseDetail$?.value;
              let prdIndex = 0;
              const syndicateTickets = await this.modalController.create({
                component: PrivateSyndicateTicketComponent,
                componentProps: { syndicate, product, prdIndex, source: "syndicateTab" },
                cssClass: "modal-fullScreen",
              });

              syndicateTickets
                .onDidDismiss()
                .then((res) => {
                  if (res?.data && res?.data?.viewMember) {
                    this.syndicateDataService.setSyndicateID(bridge.purchaseDetail$?.value?.psId);
                    this.router.navigate(["/private-syndicate/syndicate-member/" + bridge.purchaseDetail$?.value?.psId]);
                  }
                  this.router.navigate(["mainTabs/tabs/syndicate"]);
                })
                .catch((err) => {
                  this.authService.errorHandlerLogs("Private syndicate ticket modal dismiss failed:", err);
                  return err;
                });
              return await syndicateTickets?.present();
            },
            (err) => {
              this.authService.errorHandlerLogs("Error in getPrivateSyndicateProducts subscription:", err);
              return err;
            }
          );
          return;
        }
        if (page === "store" && bridge.purchaseDetail$?.value?.prosub_id) {
          this.router.navigate(["paymentConfirmation"]);
          return;
        }
      },
      (err) => {
        this.authService.errorHandlerLogs("Error in viewTicket subscription:", err);
        return err;
      }
    );
  }

  fetchSyndicateAndShowTicket(bridge: PostPaymentHostBridge): void {
    this.storeService.customOfferCashBackText$.next("");
    this.storeService.customOfferAwardId$.next(0);
    try {
      if (bridge.purchaseDetail$?.value?.isTopUp || bridge.purchaseDetail$?.value?.page === "topUp") {
        this.celebrationAnimationService.setCreditTopupStatus(false);
        this.celebrationAnimationService.trackCelebrations();
        this.router.navigateByUrl("/mainTabs/tabs/store");
        return;
      }
      const sqlAPICall = bridge.sqlAPICallResponse;
      if (sqlAPICall && sqlAPICall?.response?.status === "FAIL") {
        const defaultMsg = {
          message: {
            line_1: sqlAPICall?.response?.message ? sqlAPICall?.response?.message : "Something went wrong.",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage({}, defaultMsg);
        this.router.navigate(["mainTabs/tabs/syndicate"]);
        return;
      }

      if (!sqlAPICall || !sqlAPICall?.response) {
        this.voucherService.setSyndicatePending().pipe(take(1)).subscribe({
          error: (err) => {
            this.authService.errorHandlerLogs("Error in setSyndicatePending subscription:", err);
          },
        });
        this.matDialog.open(RegProfileDialogComponentComponent, {
          panelClass: "ongoingWarning-size",
          backdropClass: "ongoingWarning-back-drop-color",
          disableClose: true,
        });
        return;
      }
      if (sqlAPICall && sqlAPICall?.response && sqlAPICall?.response?.syndicate_type == "private") {
        this.paymentService
          .getPrivateSyndicateInfo(sqlAPICall?.response?.encrypt_syndicate_id)
          .subscribe({
            next: (syndicateData: Data) => {
              const syndicate = syndicateData?.data();
              this.syndicateService.getPrivateSyndicateProducts(syndicate?.sId).pipe(take(1)).subscribe({
                next: async (product) => {
                  const syndicateTickets = await this.modalController.create({
                    component: PrivateSyndicateTicketComponent,
                    componentProps: { syndicate, product, prdIndex: "0", source: "createPS" },
                    cssClass: "modal-fullScreen",
                  });
                  syndicateTickets
                    ?.onDidDismiss()
                    .then(() => {
                      setTimeout(() => {
                        this.router.navigateByUrl("/mainTabs/tabs/store");
                      }, 1000);
                    })
                    .catch((err) => {
                      this.authService.errorHandlerLogs("Private syndicate ticket modal dismiss failed:", err);
                      return err;
                    });
                  return await syndicateTickets.present();
                },
                error: (err) => {
                  this.authService.errorHandlerLogs("Error in getPrivateSyndicateProducts subscription:", err);
                },
              });
            },
            error: (err) => {
              this.authService.errorHandlerLogs("Error in getPrivateSyndicateInfo subscription:", err);
            },
          });
      } else {
        if (!sqlAPICall || !sqlAPICall?.response) {
          return;
        }
        this.paymentService
          .getSyndicateInfoWithProducts(sqlAPICall?.response?.encrypt_syndicate_id)
          .pipe(take(1),
            map((detail: Data) => {
              if (!Array.isArray(detail) || detail.length < 2) {
                this.authService.errorHandlerLogs("Invalid detail array structure");
                return null;
              }

              if (!detail[0] || typeof detail[0].data !== "function") {
                this.authService.errorHandlerLogs("detail[0] does not have a valid data function");
                return null;
              }

              if (detail[0].isPlaceholder) {
                this.authService.errorHandlerLogs("Received placeholder data, skipping ticket display");
                return null;
              }

              let syndicateInfo;
              try {
                syndicateInfo = detail[0].data();
              } catch (error) {
                this.authService.errorHandlerLogs("Error calling detail[0].data():", error);
                return null;
              }

              if (!syndicateInfo || typeof syndicateInfo !== "object") {
                this.authService.errorHandlerLogs("Invalid syndicateInfo returned from data()");
                return null;
              }

              syndicateInfo["product"] = [];
              const productDocs = detail?.[1]?.docs ? detail[1].docs : [];

              for (let index = 0; index < productDocs.length; index++) {
                if (productDocs[index] && typeof productDocs[index].data === "function") {
                  try {
                    syndicateInfo.product.push(productDocs[index].data());
                  } catch (error) {
                    this.authService.errorHandlerLogs("Error calling productDocs[index].data():", error);
                  }
                }
              }

              syndicateInfo["cardType"] = "Managed";
              return syndicateInfo;
            }),
            filter((syndicateInfo) => syndicateInfo !== null && syndicateInfo !== undefined)
          )
          .subscribe(
            (syndicateInfo) => {
              bridge.addNewCardInProgress = false;
              this.showTicket(syndicateInfo);
            },
            (err) => {
              this.authService.errorHandlerLogs("Error in getSyndicateInfoWithProducts subscription:", err);
              bridge.addNewCardInProgress = false;
              this.authService.getConstantsMsg("1015").then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              }).catch((error) => {
                this.authService.errorHandlerLogs("Error showing toast:", error);
              });
            }
          );
      }
    } catch (e) {
      return e;
    }
  }

  async showTicket(syndicateInfo: Data): Promise<void> {
    const syndicateTickets = await this.modalController.create({
      component: SyndicateTicketsComponent,
      componentProps: { syndicate: syndicateInfo },
      cssClass: "modal-fullScreen",
    });

    syndicateTickets
      ?.onDidDismiss()
      .then(() => {
        setTimeout(() => {
          this.router.navigateByUrl("/mainTabs/tabs/store");
        }, 1000);
      })
      .catch((err) => {
        this.authService.errorHandlerLogs("Syndicate ticket modal dismiss failed:", err);
        return err;
      });

    return await syndicateTickets?.present();
  }
}
